import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {MachineGroup} from '@models/adminstration/machine-group/machine-group';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {Machine} from '@models/machine/machine';
import {SortingService} from '@services/administration/sorting/sorting.service';
import {ArrayOperationService} from '@services/administration/array-operation/array-operation.service';
import {ElasticSearchService} from '@services/elastic-search/elastic-search.service';
import {User} from '@models/user/user';
import {startWith, takeUntil, takeWhile} from 'rxjs/operators';
import {ReplaySubject} from 'rxjs';
import {ElasticSearchResponseEntity} from '@models/elastic-search/response/elastic-search-response-entity';
import {USER_ROLE} from '@constants/user-role.constant';
import {ValidatorsService} from '@services/validators/validators.service';

@Component({
    selector: 'scitis-admin-machine-group-edit',
    templateUrl: './machine-group-edit.component.html',
    styleUrls: ['./machine-group-edit.component.scss'],
    standalone: false
})
export class MachineGroupEditComponent implements OnInit, OnDestroy {

  @Input()
  isSuperAdminRole: boolean = false;

  @Input()
  isAdminOrHigher: boolean = false;

  @Input()
  isDistributorRole: boolean = false;

  @Input()
  rootMachineGroupId: number = 0;

  @Input()
  machineGroup: MachineGroup = new MachineGroup();

  @Input()
  loggedUser: User = new User();

  machineGroupForm: UntypedFormGroup = new UntypedFormGroup({});

  isNewMachineGroup: boolean = false;
  isLoadingParents: boolean = false;
  isLoadingMachines: boolean = false;
  submitted: boolean = false;
  selected: Machine = new Machine();

  possibleParents: MachineGroup[] = new Array<MachineGroup>();
  possibleMachines: Machine[] = new Array<Machine>();

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
      private activeModal: NgbActiveModal,
      private elasticSearchService: ElasticSearchService,
      private sortingService: SortingService,
      private arrayOperationService: ArrayOperationService,
      private validatorsService: ValidatorsService
  ) {
  }

  get f() {
    return this.machineGroupForm.controls;
  }

  get isDisabledSaveBtn(): boolean {
    return this.submitted ? this.machineGroupForm.invalid : this.submitted && this.machineGroupForm.invalid;
  };

  get machineIsNotEmpty(): boolean {
    return this.selected?.name !== '';
  }

  ngOnInit(): void {
    this.isNewMachineGroup = (!this.machineGroup || this.machineGroup.id <= 0);
    this.getPossibleParents(this.isSuperAdminRole);
    this.getPossibleMachines();
    this.generateMachineGroupForm(this.machineGroup);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  onClose() {
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  onCancel() {
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }

  onSave() {
    this.submitted = true;
    // stop here if form is invalid
    this.machineGroupForm.statusChanges.pipe(
        startWith(this.machineGroupForm.status),
        takeWhile(status => status === 'VALID' || status === 'PENDING')
    ).subscribe((status) => {
      if (status === 'VALID') {
        this.updateMachineGroupProperties();
        this.activeModal.close(this.machineGroup);
      }
    });
  }

  public shouldHideParentDropDown(): boolean {
    return !this.isAdminOrHigher && (this.rootMachineGroupId === this.machineGroup.id) && !this.isDistributorRole;
  }

  onParentChange(machineGroupParent: MachineGroup | undefined) {
    if (machineGroupParent === undefined) {
      this.machineGroup.parentName = '';
    } else {
      this.machineGroup.parentName = machineGroupParent.name;
    }
  }

  onSelectedMachine() {
    if (this.selected && this.machineIsNotEmpty) {
      if (!this.machineAlreadyAdded()) {
        this.machineGroup.machines.push(this.selected);
        this.possibleMachines = this.possibleMachines.filter(element => element.id !== this.selected?.id);
      }
    }
    this.selected = new Machine();
  }

  onRemoveMachine(machine: Machine) {
    console.log(machine.name + ' removed from machinery group');
    const index: number = this.machineGroup.machines.indexOf(machine);
    this.machineGroup.machines.splice(index, 1);
    this.possibleMachines.push(machine);
    this.possibleMachines = [...this.possibleMachines.sort((a, b) => a.type < b.type ? -1 : 1)];
  }

  private machineAlreadyAdded(): boolean {
    return this.arrayOperationService.exists(this.selected, this.machineGroup.machines, 'id');
  }

  private updateMachineGroupProperties() {
    this.machineGroup.name = this.f.name.value;
    this.machineGroup.parentId = this.f.parentId.value;

    if (this.machineGroup.parentName === null || this.machineGroup.parentName === undefined) {
      this.machineGroup.parentName = '';
    }
    if (this.machineGroup.machines === null || this.machineGroup.machines === undefined) {
      this.machineGroup.machines = [];
    }
  }

  private getPossibleParents(isSuperAdminRole: boolean): void {
    this.isLoadingParents = true;
    const groupId: number | null = this.loggedUser.machineGroup;
    const whiteListedMachineryGroups: number[] | null = !isSuperAdminRole ? this.loggedUser.getWhiteListedMachineryGroups(true) : [];

    this.elasticSearchService.listAllMachineryGroups(groupId, whiteListedMachineryGroups)
    .pipe(takeUntil(this.destroyed$)).subscribe(
        {
          next: (success: ElasticSearchResponseEntity) => {
            if (success?.result?.data && success.result.data.length > 0) {
              this.possibleParents = this.addNoParentOption(success.result.data, isSuperAdminRole);
            }
            this.isLoadingParents = false;
          },
          error: (error) => {
            console.error('MachineGroup Modal. Error loading the list of possible machinery group parents', JSON.stringify(error));
            this.possibleParents = this.addNoParentOption(null, isSuperAdminRole);
            this.isLoadingParents = false;
          }
        });
  }

  private getPossibleMachines(): void {
    this.isLoadingMachines = true;
    const organizationId = this.loggedUser.role == USER_ROLE.SUPERADMIN ? -1 : this.loggedUser.parentId;
    this.elasticSearchService.listAllMachines(organizationId, this.rootMachineGroupId, this.loggedUser.getWhiteListedMachineryGroups(true))
    .pipe(takeUntil(this.destroyed$)).subscribe({
      next: (success: ElasticSearchResponseEntity) => {
        if (success?.result?.data && success.result.data.length > 0) {
          this.possibleMachines = success.result.data
          .filter(({id}) => !this.machineGroup.machines.some(item => item.id === id))
          .sort((a, b) => a.type < b.type ? -1 : 1);
        }
        this.isLoadingMachines = false;
      }, error: (error) => {
        console.error('MachineGroup Modal. Error loading the list of possible machines', JSON.stringify(error));
        this.possibleMachines = new Array<Machine>();
        this.isLoadingMachines = false;
      }
    });
  }

  private addNoParentOption(parents: MachineGroup[] | null, isSuperAdminRole: boolean): MachineGroup[] {
    if (parents === null) {
      parents = new Array<MachineGroup>();
    }

    let noParentOptionNotAdded = true;
    parents.forEach(m => {
      if (m.id === -1) {
        noParentOptionNotAdded = false;
      }
    });

    if (isSuperAdminRole && noParentOptionNotAdded) {
      const noParentOption = new MachineGroup();
      noParentOption.id = -1;
      noParentOption.name = ' NONE';
      parents.push(noParentOption);
    }
    parents = parents.sort((a, b) => this.sortingService.sortByName(a, b));
    return parents;
  }

  private generateMachineGroupForm(machineGroup: MachineGroup) {
    this.machineGroupForm = new UntypedFormGroup({
      name: new UntypedFormControl(machineGroup.name, [Validators.required], [this.validatorsService.duplicateMachineryGroupName(this.machineGroup.name)]),
      externalId: new UntypedFormControl(machineGroup.externalId),
      parentName: new UntypedFormControl(machineGroup.parentName),
      parentId: new UntypedFormControl(machineGroup.parentId, [Validators.required]),
      machines: new UntypedFormControl(machineGroup.machines),
    });
    if (!this.isAdminOrHigher && !this.isDistributorRole) {
      this.machineGroupForm.disable();
    }
  }
}
